import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { CONTACT, ENQUIRY_CATEGORIES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", organisation: "", email: "", category: "", message: "" };

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.category) {
            toast.error("Please choose an enquiry type.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Thank you. Your enquiry has reached MS Group — our team will respond shortly.");
            setForm(initialForm);
        } catch (err) {
            toast.error("Something went wrong. Please try again or write to us directly.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full border border-[#DDDAD2] bg-white px-4 py-3.5 text-[15px] text-[#0F172A] outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-[#F16622]";

    return (
        <main data-testid="contact-page">
            <SEO
                title="Contact Us | MS Group — Hyderabad, India"
                description="Get in touch with MS Group — 5th Floor, NCL Ganga Apartments, Quthbullapur, Secunderabad, Telangana 500067. Call +91 63039 01535 or email team@msgroupin.com."
            />
            <section className="grain relative flex min-h-[100svh] flex-col justify-center overflow-hidden bg-[#0A0D20] pb-16 pt-36 md:pb-20 md:pt-44" data-testid="contact-hero">
                <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <Reveal>
                        <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#F16622]">
                            <span className="inline-block h-px w-10 bg-[#F16622]" />
                            Contact Us
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="font-display mt-7 max-w-3xl text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                            Let's start a conversation.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-7 max-w-xl text-base leading-relaxed text-white/65 md:text-lg">
                            Whether it's a partnership, a career, or a new business opportunity — the corporate team at
                            MS Group would be glad to hear from you.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-[#F8F7F3] py-14 md:py-20">
                <div className="mx-auto grid max-w-[1440px] gap-12 px-6 md:px-12 lg:grid-cols-[1fr_1.2fr] lg:px-20">
                    <div className="space-y-8">
                        <Reveal>
                            <div className="bg-[#0A0D20] p-10 text-white" data-testid="contact-office-card">
                                <h2 className="font-display text-2xl font-medium">Corporate Office</h2>
                                <div className="mt-8 space-y-6 text-[15px]">
                                    <p className="flex items-start gap-4">
                                        <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#F16622]" strokeWidth={1.5} />
                                        <span className="leading-relaxed text-white/75">
                                            {CONTACT.office}
                                            <br />
                                            {CONTACT.addressLines.map((a) => (
                                                <span key={a}>
                                                    {a}
                                                    <br />
                                                </span>
                                            ))}
                                        </span>
                                    </p>
                                    <p className="flex items-center gap-4">
                                        <Mail className="h-5 w-5 shrink-0 text-[#F16622]" strokeWidth={1.5} />
                                        <a href={`mailto:${CONTACT.email}`} className="text-white/75 transition-colors hover:text-white" data-testid="contact-email-link">
                                            {CONTACT.email}
                                        </a>
                                    </p>
                                    <p className="flex items-center gap-4">
                                        <Phone className="h-5 w-5 shrink-0 text-[#F16622]" strokeWidth={1.5} />
                                        <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="text-white/75 transition-colors hover:text-white" data-testid="contact-phone-link">
                                            {CONTACT.phone}
                                        </a>
                                    </p>
                                    <p className="flex items-center gap-4">
                                        <Clock className="h-5 w-5 shrink-0 text-[#F16622]" strokeWidth={1.5} />
                                        <span className="text-white/75">{CONTACT.hours}</span>
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="overflow-hidden border border-[#E7E5DE]" data-testid="contact-map">
                                <iframe
                                    title="MS Group Corporate Office — Hyderabad"
                                    src={CONTACT.mapEmbed}
                                    className="h-72 w-full grayscale-[35%]"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.08}>
                        <form onSubmit={submit} className="border border-[#E7E5DE] bg-white p-8 md:p-12" data-testid="enquiry-form">
                            <h2 className="font-display text-2xl font-medium tracking-tight text-[#0F172A]">Send an enquiry</h2>
                            <p className="mt-3 text-sm leading-relaxed text-slate-500">
                                General enquiries, business partnerships, corporate communications, careers and new
                                business opportunities — all land here.
                            </p>

                            <div className="mt-10 grid gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="enquiry-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Full Name *
                                    </label>
                                    <input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="enquiry-organisation" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Organisation
                                    </label>
                                    <input id="enquiry-organisation" data-testid="enquiry-organisation-input" value={form.organisation} onChange={set("organisation")} placeholder="Company / Institution" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="enquiry-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Email *
                                    </label>
                                    <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="enquiry-category" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Enquiry Type *
                                    </label>
                                    <select id="enquiry-category" data-testid="enquiry-category-select" required value={form.category} onChange={set("category")} className={`${inputCls} appearance-none`}>
                                        <option value="" disabled>
                                            Select a category
                                        </option>
                                        {ENQUIRY_CATEGORIES.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="enquiry-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Message *
                                    </label>
                                    <textarea id="enquiry-message" data-testid="enquiry-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Tell us how we can help…" className={`${inputCls} resize-none`} />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="enquiry-submit-btn"
                                className="group mt-10 inline-flex items-center gap-3 bg-[#232B6B] px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#F16622] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {sending ? "Sending…" : "Submit Enquiry"}
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                        </form>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
